import { createRoute } from '@tanstack/react-router';
import { lazy } from 'react';
import { appLayoutRoute, portalLayoutRoute } from '../../router/parents';
import { isPortalTab, type PortalTab } from './lib';

// Screens load on demand (React.lazy; the router renders each match inside Suspense).
const PortalAccessPage = lazy(() => import('./admin/pages').then((m) => ({ default: m.PortalAccessPage })));
const PortalAdminLayout = lazy(() => import('./admin/pages').then((m) => ({ default: m.PortalAdminLayout })));
const PortalProjectAdminPage = lazy(() =>
  import('./admin/pages').then((m) => ({ default: m.PortalProjectAdminPage })),
);
const PortalInboxPage = lazy(() => import('./admin/inbox').then((m) => ({ default: m.PortalInboxPage })));
const PortalThreadPage = lazy(() => import('./admin/inbox').then((m) => ({ default: m.PortalThreadPage })));
const PortalPreviewPage = lazy(() =>
  import('./admin/preview').then((m) => ({ default: m.PortalPreviewPage })),
);
const PortalEntryPage = lazy(() => import('./client/pages').then((m) => ({ default: m.PortalEntryPage })));
const PortalProjectsPage = lazy(() =>
  import('./client/pages').then((m) => ({ default: m.PortalProjectsPage })),
);
const PortalSignInRoutePage = lazy(() =>
  import('./client/pages').then((m) => ({ default: m.PortalSignInRoutePage })),
);
const PortalWorkspaceLayout = lazy(() =>
  import('./client/pages').then((m) => ({ default: m.PortalWorkspaceLayout })),
);
const PortalChangeOrderPage = lazy(() =>
  import('./client/project').then((m) => ({ default: m.PortalChangeOrderPage })),
);
const PortalInvoicePage = lazy(() =>
  import('./client/project').then((m) => ({ default: m.PortalInvoicePage })),
);
const PortalProjectPage = lazy(() =>
  import('./client/project').then((m) => ({ default: m.PortalProjectPage })),
);

/**
 * Client portal routes, mounted under /portal (portal layout).
 *
 *   /portal                                   pick a portal / sign in without a workspace link
 *   /portal/$slug                             client side, branded per workspace (magic-link auth)
 *     /sign-in, /projects/$projectId[?tab=], /projects/$projectId/invoices/$invoiceId,
 *     /projects/$projectId/change-orders/$changeOrderId
 *   /portal/admin                             team side (app shell): access, sharing, selections, inbox, preview
 *
 * `admin` is a reserved workspace slug, so it never collides with `$slug`.
 */

const portalIndexRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: '/',
  component: PortalEntryPage,
});

// ---------------------------------------------------------------------------
// Team side
// ---------------------------------------------------------------------------

const adminRoute = createRoute({
  // Team side lives in the signed-in app shell (sidebar), whose layout already requires a session.
  getParentRoute: () => appLayoutRoute,
  path: '/portal/admin',
  component: PortalAdminLayout,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: PortalAccessPage,
});
const adminProjectRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'projects/$projectId',
  component: PortalProjectAdminPage,
});
const adminInboxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inbox',
  component: PortalInboxPage,
});
const adminThreadRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'inbox/$projectId',
  component: PortalThreadPage,
});
const adminPreviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'preview/$projectId',
  validateSearch: (search: Record<string, unknown>): { contactId?: string } =>
    typeof search.contactId === 'string' && /^[0-9a-f-]{36}$/i.test(search.contactId)
      ? { contactId: search.contactId }
      : {},
  component: PortalPreviewPage,
});

// ---------------------------------------------------------------------------
// Client side
// ---------------------------------------------------------------------------

const workspacePortalRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: '$slug',
  component: PortalWorkspaceLayout,
});

const workspacePortalIndexRoute = createRoute({
  getParentRoute: () => workspacePortalRoute,
  path: '/',
  component: PortalProjectsPage,
});
const workspacePortalSignInRoute = createRoute({
  getParentRoute: () => workspacePortalRoute,
  path: 'sign-in',
  validateSearch: (search: Record<string, unknown>): { email?: string } =>
    typeof search.email === 'string' && search.email.length <= 320 ? { email: search.email } : {},
  component: PortalSignInRoutePage,
});
const portalProjectRoute = createRoute({
  getParentRoute: () => workspacePortalRoute,
  path: 'projects/$projectId',
  validateSearch: (search: Record<string, unknown>): { tab?: PortalTab } =>
    isPortalTab(search.tab) ? { tab: search.tab } : {},
  component: PortalProjectPage,
});
const portalInvoiceRoute = createRoute({
  getParentRoute: () => workspacePortalRoute,
  path: 'projects/$projectId/invoices/$invoiceId',
  validateSearch: (search: Record<string, unknown>): { payment?: 'success' | 'cancelled' } =>
    search.payment === 'success' || search.payment === 'cancelled' ? { payment: search.payment } : {},
  component: PortalInvoicePage,
});
const portalChangeOrderRoute = createRoute({
  getParentRoute: () => workspacePortalRoute,
  path: 'projects/$projectId/change-orders/$changeOrderId',
  component: PortalChangeOrderPage,
});

/** Team-side portal management, mounted inside the signed-in app layout. */
export const portalAdminRoutes = [
  adminRoute.addChildren([
    adminIndexRoute,
    adminProjectRoute,
    adminInboxRoute,
    adminThreadRoute,
    adminPreviewRoute,
  ]),
] as const;

export const portalRoutes = [
  portalIndexRoute,
  workspacePortalRoute.addChildren([
    workspacePortalIndexRoute,
    workspacePortalSignInRoute,
    portalProjectRoute,
    portalInvoiceRoute,
    portalChangeOrderRoute,
  ]),
] as const;
